import { TicketBasket } from "@shared/modules/basket/types";
import { DataProvider } from "@shared/constants/common";
import { isAxiosError } from "@shared/utils/errors/error-utils";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../services/useBasketPaymentService";
import useInvoicePaymentService from "../services/useInvoicePaymentService";
import { PurchaseOptsType } from "../types";
import type { PaymentManagerConfig } from "../PaymentManager";
import { PaymentConfigurationError } from "../utils/errors/PaymentConfigurationError";
import { PaymentConfirmationError } from "../utils/errors/PaymentConfirmationError";
import { PaymentProcessingError } from "../utils/errors/PaymentProcessingError";
import { PaymentSystemError } from "../utils/errors/PaymentSystemError";
import { isPaymentOrchestrationError } from "../utils/errorUtils";
import { createPaymentLogger } from "../utils/payment-logger";
import { recordPaymentError } from "../utils/errors/recordPaymentError";

const log = createPaymentLogger("PaymentGateway");

export type UpdatePaymentObjectOptions = {
  confirmationStrategy?: PAYMENT_MANAGER_CONFIRMATION_STRATEGY;
  forceUpsert?: boolean;
};

/* eslint-disable @typescript-eslint/no-unused-vars, class-methods-use-this */
class PaymentGateway {
  config: PaymentManagerConfig;

  /**
   * A static property that uniquely identifies the payment gateway.
   * This property must be defined in all subclasses.
   */
  public static readonly gatewayName: string;

  protected basketPaymentService?: ReturnType<typeof useBasketPaymentService>;
  protected invoicePaymentService?: ReturnType<typeof useInvoicePaymentService>;

  constructor(
    config: PaymentManagerConfig,
    servicesContext: {
      basketPaymentService?: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService?: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    this.config = config;
    this.basketPaymentService = servicesContext.basketPaymentService;
    this.invoicePaymentService = servicesContext.invoicePaymentService;

    if (
      !Object.prototype.hasOwnProperty.call(this.constructor, "gatewayName")
    ) {
      throw new PaymentConfigurationError(
        `Class ${this.constructor.name} must define a static 'gatewayName' property.`
      );
    }
  }

  /**
   * An instance of the internal Gateway object being used
   */
  instance: unknown;

  /**
   * An optional instance that manages the Gateway's UI elements
   */
  elementsInstance?: unknown;

  /**
   * Optional callback to signal processing state to the UI.
   * Propagated from PaymentManager so gateway-driven flows (e.g. Yuno wallet)
   * can toggle the same `isProcessingPurchase` flag that PaymentManager.purchase() uses.
   */
  onProcessing?: (isProcessing: boolean) => void;

  /**
   * Callback invoked by the gateway when a wallet SDK authorizes a payment
   * (e.g. Yuno Apple Pay / Google Pay). The gateway passes the one-time token
   * to the manager, which orchestrates the full purchase flow. This replaces
   * the old pattern where the gateway self-orchestrated wallet purchases.
   */
  onWalletPaymentAuthorized?: (oneTimeToken: string) => Promise<void>;

  /**
   * Creates a fresh checkout session and updates the SDK instance.
   * Called after payment failures so the user can retry without a stale session.
   * Gateways that don't need session renewal can leave this undefined.
   */
  renewCheckoutSession?(): Promise<void>;

  /**
   * Logic to initialize the gateway
   */
  initialize(basket: PaymentServiceBasket, ...args: unknown[]): Promise<this> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the initialize method`
    );
  }

  savePaymentMethod(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ): Promise<unknown> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the savePaymentMethod method`
    );
  }

  confirmPayment(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ): Promise<unknown> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the confirmPayment method`
    );
  }

  confirmDeferredPayment(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ): Promise<unknown> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the confirmDeferredPayment method`
    );
  }

  /**
   * Logic to update the objects used by this payment processor that are related to payments.
   * The optional options bag is passed during purchase flows so gateways can
   * decide behaviour based on the confirmation strategy or force-create objects.
   */
  updatePaymentObject(
    basket: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "basketUuid2" | "currency" | "totalPrice"
    >,
    _options?: UpdatePaymentObjectOptions
  ): Promise<this> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the updatePaymentObject method`
    );
  }

  postProcessCheckout(): Promise<TicketBasket> {
    throw new PaymentSystemError(
      `${this.constructor.name} must implement the postProcessCheckout method`
    );
  }

  /**
   * Classifies a raw error from a purchase/confirm call into the payment error taxonomy.
   * Re-throws pre-classified PaymentOrchestrationErrors unchanged; wraps Axios responses
   * and unknown errors into PaymentConfirmationError or PaymentProcessingError.
   */
  protected handleConfirmError(error: unknown): never {
    if (isPaymentOrchestrationError(error)) {
      throw error;
    }

    const { gatewayName } = this.constructor as typeof PaymentGateway;
    const isAxios = isAxiosError(error);

    log.error("Purchase API error", {
      isAxios,
      httpStatus: isAxios ? error.response?.status : undefined,
      errorCode: isAxios ? error.code : undefined,
      hasMessage: error instanceof Error ? Boolean(error.message) : false,
    });

    if (isAxios) {
      const status = error.response?.status;
      const responseData = error.response?.data;
      const detail = responseData?.detail;

      if (typeof detail === "string") {
        // TODO: Remove this once we have a better way to handle this error
        if (detail.includes("expected a dictionary")) {
          recordPaymentError(error);
        }

        throw new PaymentConfirmationError(detail, {
          metadata: {
            error: detail,
            httpStatus: status,
            responseData,
            errorCode: error.code,
            gatewayName,
            operation: "purchase",
          },
          userMessage: detail,
        });
      }

      throw new PaymentProcessingError(
        `${gatewayName} purchase failed with HTTP ${status}`,
        {
          metadata: {
            httpStatus: status,
            responseData,
            errorCode: error.code,
            gatewayName,
            operation: "purchase",
          },
          userMessage: "We had trouble processing this transaction",
        }
      );
    }

    let fallbackMessage = "We had trouble processing this transaction";
    if (error instanceof Error) {
      fallbackMessage = error.message;
    } else if (typeof error === "string") {
      fallbackMessage = error;
    } else if (error && typeof error === "object" && "message" in error) {
      const msg = (error as { message?: string }).message;
      if (msg) {
        fallbackMessage = msg;
      }
    }

    throw new PaymentProcessingError(
      `${gatewayName} purchase error: ${fallbackMessage}`,
      {
        metadata: {
          gatewayName,
          operation: "purchase",
          originalError: fallbackMessage,
        },
        userMessage: "We had trouble processing this transaction",
      }
    );
  }

  // Method to update services
  setServices(services: {
    basketPaymentService: ReturnType<typeof useBasketPaymentService>;
    invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
  }): void {
    this.basketPaymentService = services.basketPaymentService;
    this.invoicePaymentService = services.invoicePaymentService;
  }
}

export default PaymentGateway;
